import React, { useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  Button,
  Tag,
  IconButton,
  HeadingText,
  BodyText,
  SelectMenu,
  ActionMenu,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TextInput,
  showToast,
} from '@spotoninc/nexus-react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  MoreHorizontalIcon,
  WarningIcon,
  AddIcon,
} from '@spotoninc/nexus-web-assets';
import { DateRangePicker, Uploader } from 'rsuite';
import { checkConnection } from './integrationUtils';
import store from '../../../redux/store';
import BlankIntegration from '../../../media/images/blankIntegration.png';
import { getAuthHeader } from '../../../utils/tokenService';
import { useFeatureFlagFromContext } from '../../../launchdarkly';

/**
 * Reskinned Integration List Component based on Nexus design system
 * This component is only rendered when the showReskin feature flag is true
 */
const ReskinnedIntegrationList = ({
  apps,
  onAddIntegration,
  onItemClick,
  onPeriodCalTypeChange,
  onCashAcChange,
  onAppNameChange,
  onEditClick,
  onBudgetSettingsClick,
  onAuthClick,
  onDisconnectClick,
  onDateRangeChange,
  onUploaderChange,
  onResyncClick,
  editButtonLoading,
  syncButtonLoading,
  periodCalTypeFlagInt,
  setPeriodCalTypeFlagInt,
  sendEditDataFlag,
  setSendEditDataFlag,
  setOnEditComplete,
  onSetIntegrationId,
  enableBudgeting,
  yearBasedBudgeting,
  propotionalBudgeting,
  locationClassLevelBudgeting,
  limitUploader,
  excelUploadData,
  country,
  periodTypes,
  cashAccData,
  companySwitcherActive,
  apiEndpoint,
  returnCurrencyFromCountry,
}) => {
  // Feature flags
  const slimFeaturesOnly = useFeatureFlagFromContext('slimFeaturesOnly', false);
  const simplifiedIntegrationFlow = useFeatureFlagFromContext(
    'simplifiedIntegrationFlow',
    false
  );

  // State to track expanded items - moved to the top before any conditional returns
  const [expandedItems, setExpandedItems] = useState({});
  // State to track edited integration names per item
  const [editedAppNames, setEditedAppNames] = useState({});
  // State to track which items are currently saving
  const [savingItems, setSavingItems] = useState({});

  // Toggle expanded state for an item
  const toggleExpanded = itemId => {
    const isCurrentlyExpanded = expandedItems[itemId];

    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));

    // Only call onItemClick to update the parent component's state
    // This doesn't need to trigger an API call since we're just displaying existing data
    const selectedItem = apps.find(item => item.id === itemId);
    if (selectedItem && !isCurrentlyExpanded) {
      // Only call onItemClick when expanding, not when collapsing
      // This ensures we have the correct item selected for any future edits
      onItemClick(selectedItem);
      // Pre-populate the edited app name with the current value when expanding
      setEditedAppNames(prev => ({
        ...prev,
        [itemId]: selectedItem.app_name,
      }));
    } else if (isCurrentlyExpanded) {
      // Clean up the edited app name when collapsing to prevent memory leaks
      setEditedAppNames(prev => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle integration name change
  const handleAppNameChange = (itemId, value) => {
    setEditedAppNames(prev => ({
      ...prev,
      [itemId]: value,
    }));
  };

  // Save integration name
  const saveAppName = item => {
    const rawName = editedAppNames[item.id];
    const newName = rawName?.trim();

    // Validate: check if name is empty or only whitespace
    if (!newName) {
      showToast({
        priority: 'error',
        content: 'Integration name cannot be empty',
        autoClose: 5000,
      });
      return;
    }

    // Validate: check if name is unchanged
    if (newName === item.app_name) {
      showToast({
        priority: 'success',
        content: 'No changes to save',
        autoClose: 5000,
      });
      return;
    }

    // Set saving state for this item
    setSavingItems(prev => ({ ...prev, [item.id]: true }));

    if (typeof onAppNameChange === 'function') {
      onAppNameChange(newName);
    }

    // Register callback to handle API completion, keyed by integration ID to prevent race conditions
    if (typeof setOnEditComplete === 'function') {
      setOnEditComplete(item.id, result => {
        // Clear saving state
        setSavingItems(prev => {
          const { [item.id]: _, ...rest } = prev;
          return rest;
        });

        if (result?.success) {
          // Clear the edited name since it's now saved
          setEditedAppNames(prev => {
            const { [item.id]: _, ...rest } = prev;
            return rest;
          });
        } else {
          // Show error toast on failure
          showToast({
            priority: 'error',
            content: 'Failed to save integration name',
            autoClose: 5000,
          });
        }
      });
    }

    // Set the integration ID to ensure the correct integration is updated
    // This prevents race conditions when multiple integrations are expanded
    if (typeof onSetIntegrationId === 'function') {
      onSetIntegrationId(item.id);
    }

    // Trigger the API call - React 18's automatic batching ensures state is updated
    setSendEditDataFlag(prev => !prev);
  };

  // Check if app name has changed for an item
  const hasAppNameChanged = item => {
    const editedName = editedAppNames[item.id];
    return editedName !== undefined && editedName.trim() !== item.app_name;
  };

  // Check if the edited app name is valid (not empty or whitespace-only)
  const isAppNameValid = item => {
    const editedName = editedAppNames[item.id];
    return editedName !== undefined && editedName.trim().length > 0;
  };
  // Welcome message for new users - Figma design implementation
  if (apps !== undefined && apps.length === 0) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap="24px"
      >
        {/* Integration illustration */}
        <Box width="13.9375rem" height="10.27556rem">
          <img
            src={BlankIntegration}
            alt="Integration illustration"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        </Box>

        {/* Text content */}
        <Flex direction="column" alignItems="center" gap="8px">
          <HeadingText
            style={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '1.333em',
              textAlign: 'center',
              color: 'var(--semantic-color-text-neutral-strongest-none)',
            }}
          >
            Get started by connecting your accounting system
          </HeadingText>
          <BodyText
            style={{
              fontFamily: 'Poppins',
              fontSize: '16px',
              lineHeight: '1.5em',
              textAlign: 'center',
              color: 'var(--semantic-color-text-neutral-strongest-none)',
            }}
          >
            Connect your accounting software below. You'll receive your first profitability insights from profitassist@spoton.com within 48 hours.
          </BodyText>
        </Flex>

        {/* Add Integration button */}
        <Button
          variant="filled"
          onClick={onAddIntegration}
          style={{
            backgroundColor:
              'var(--semantic-color-container-brand-strong-none)',
            borderRadius: '96px',
            padding: '10px 24px',
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '1.5em',
            letterSpacing: '0.1px',
          }}
          leadingIcon={function () {
            return <AddIcon size="1.5rem" />;
          }}
        >
          Add Integration
        </Button>
      </Flex>
    );
  }

  if (!apps) {
    return null;
  }

  return (
    <Flex direction="column" gap="24px">
      {apps.map(item => {
        const isExpanded = expandedItems[item.id] || false;
        const isConnected = checkConnection(item);

        return (
          <Box
            key={item.id}
            borderRadius="16px"
            bg="var(--semantic-color-surface-neutral-dim-none)"
            style={{ padding: '24px' }}
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              onClick={() => toggleExpanded(item.id)}
              style={{ cursor: 'pointer' }}
            >
              <Flex alignItems="center" gap="16px">
                {isExpanded ? (
                  <ChevronDownIcon size="1.5rem" color="#0F1221" />
                ) : (
                  <ChevronRightIcon size="1.5rem" color="#0F1221" />
                )}
                <Text
                  fontFamily="Poppins"
                  fontWeight={600}
                  fontSize="18px"
                  lineHeight="1.333em"
                  color="var(--semantic-color-text-neutral-strongest-none)"
                >
                  {item.app_name}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="16px">
                {isConnected ? (
                  <Tag variant="success" emphasis="subtle">
                    Connected to {item.integration_desc}
                  </Tag>
                ) : (
                  <Flex alignItems="center" gap="8px">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        // Check if this is Quickbooks Online or another integration type
                        const isQuickbooks =
                          item.integration_desc &&
                          item.integration_desc
                            .toLowerCase()
                            .includes('quickbooks');

                        if (isQuickbooks) {
                          // Pass the full item object for analytics tracking
                          onAuthClick(item);
                        } else {
                          // Use onResyncClick for other integrations like Xero
                          onResyncClick(item);
                        }
                      }}
                    >
                      Connect to {item.integration_desc}
                    </Button>
                    <Tooltip placement="top">
                      <TooltipTrigger>
                        <WarningIcon
                          size="1.5rem"
                          color="#F59E0B"
                          style={{ cursor: 'pointer' }}
                        />
                      </TooltipTrigger>
                      <TooltipContent className="tooltip-container">
                        <Text className="tooltip-title">
                          Action required to complete setup
                        </Text>
                        <Text className="tooltip-body">
                          Integration details have been saved. Please continue
                          connecting to {item.integration_desc} to complete the
                          setup.
                        </Text>
                      </TooltipContent>
                    </Tooltip>
                  </Flex>
                )}
                {/* Only show More options menu if there are options to show */}
                {/* Edit Budget Settings: only when slimFeaturesOnly is true */}
                {/* Disconnect: only when isConnected is true */}
                {(slimFeaturesOnly || isConnected) && (
                  <ActionMenu
                    options={[
                      // Only show Edit Budget Settings when slimFeaturesOnly is true
                      ...(slimFeaturesOnly
                        ? [
                            {
                              label: 'Edit Budget Settings',
                              onClick: e => {
                                e.stopPropagation();
                                onBudgetSettingsClick(item.id);
                              },
                            },
                          ]
                        : []),
                      // Only show Disconnect option if the integration is connected
                      ...(isConnected
                        ? [
                            {
                              label: 'Disconnect',
                              onClick: e => {
                                e.stopPropagation();
                                onDisconnectClick(item.id);
                              },
                            },
                          ]
                        : []),
                    ]}
                  >
                    {({ getRootProps }) => (
                      <IconButton
                        {...getRootProps()}
                        icon={MoreHorizontalIcon}
                        variant="text"
                        label="More options"
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                  </ActionMenu>
                )}
              </Flex>
            </Flex>

            {/* Expanded content */}
            {isExpanded && (
              <Box mt={4} ml={8}>
                {/* Integration details row - all items in a single row with wrapping */}
                <Flex
                  direction="row"
                  gap={4}
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  padding="0 0 0 40px"
                  mb={6}
                  alignItems={'center'}
                >
                  {/* Integration Name - editable only when simplifiedIntegrationFlow is enabled */}
                  {simplifiedIntegrationFlow ? (
                    <Flex direction="column" width="25rem" margin="0 0 16px 0">
                      <BodyText
                        emphasis
                        style={{
                          color:
                            'var(--semantic-color-text-neutral-lightest-none)',
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          letterSpacing: '0.2px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        Integration name
                      </BodyText>
                      <TextInput
                        value={editedAppNames[item.id] ?? item.app_name}
                        placeholder="Enter integration name"
                        style={{ height: '40px', width: '100%' }}
                        onChange={e =>
                          handleAppNameChange(item.id, e.target.value)
                        }
                      />
                    </Flex>
                  ) : (
                    <Flex direction="column" width="25rem" margin="0 0 16px 0">
                      <BodyText
                        emphasis
                        style={{
                          color:
                            'var(--semantic-color-text-neutral-lightest-none)',
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          letterSpacing: '0.2px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        Integration name
                      </BodyText>
                      <BodyText
                        style={{
                          fontFamily: 'Poppins',
                          fontSize: '16px',
                          color:
                            'var(--semantic-color-text-neutral-strongest-none)',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {item.app_name}
                      </BodyText>
                    </Flex>
                  )}

                  {/* Date of last sync */}
                  <Flex direction="column" width="25rem" margin="0 0 16px 0">
                    <BodyText
                      emphasis
                      style={{
                        color:
                          'var(--semantic-color-text-neutral-lightest-none)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Date of last sync
                    </BodyText>
                    <BodyText
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        color:
                          'var(--semantic-color-text-neutral-strongest-none)',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.date_updated}
                    </BodyText>
                  </Flex>

                  {/*Accounting Type */}
                  <Flex direction="column" width="25rem" margin="0 0 16px 0">
                    <BodyText
                      emphasis
                      style={{
                        color:
                          'var(--semantic-color-text-neutral-lightest-none)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Accounting type
                    </BodyText>
                    <SelectMenu
                      options={[
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Accrual', label: 'Accrual' },
                      ]}
                      initialValue={item.cash_accrual}
                      style={{ height: '40px', width: '100%' }}
                      onChange={(_, value) => {
                        // No need to call onItemClick here as the item is already selected
                        // when the accordion is expanded

                        // Validate the value before updating and triggering API call
                        if (!value || value === '') {
                          return;
                        }

                        // Update the cashAcInt value
                        onCashAcChange(value);

                        // Trigger the API call to save the changes
                        // React 18's automatic batching handles state updates
                        setSendEditDataFlag(prev => !prev);
                      }}
                    />
                  </Flex>

                  {/* Integration type */}
                  <Flex direction="column" width="25rem" margin="0 0 16px 0">
                    <BodyText
                      emphasis
                      style={{
                        color:
                          'var(--semantic-color-text-neutral-lightest-none)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Integration type
                    </BodyText>
                    <BodyText
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        color:
                          'var(--semantic-color-text-neutral-strongest-none)',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.integration_desc}
                    </BodyText>
                  </Flex>

                  {/* Currency */}
                  <Flex direction="column" width="25rem" margin="0 0 16px 0">
                    <BodyText
                      emphasis
                      style={{
                        color:
                          'var(--semantic-color-text-neutral-lightest-none)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Currency
                    </BodyText>
                    <BodyText
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        color:
                          'var(--semantic-color-text-neutral-strongest-none)',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {returnCurrencyFromCountry(item.country, country)}
                    </BodyText>
                  </Flex>

                  {/* Accounting Format */}
                  <Flex direction="column" width="25rem" margin="0 0 16px 0">
                    <BodyText
                      emphasis
                      style={{
                        color:
                          'var(--semantic-color-text-neutral-lightest-none)',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        fontWeight: 500,
                        letterSpacing: '0.2px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Accounting Format
                    </BodyText>
                    <SelectMenu
                      options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'periods', label: 'Periods' },
                      ]}
                      initialValue={item.period_cal ? 'periods' : 'monthly'}
                      style={{
                        height: '40px',
                        width: '100%',
                      }}
                      onChange={(_, value) => {
                        // No need to call onItemClick here as the item is already selected
                        // when the accordion is expanded

                        // Validate the value before updating and triggering API call
                        if (!value || value === '') {
                          console.error('Cannot set empty Accounting Format');
                          return;
                        }

                        // Set periodCalTypeFlagInt based on selected accounting format
                        if (value === 'monthly') {
                          setPeriodCalTypeFlagInt(false);
                        } else if (value === 'periods') {
                          setPeriodCalTypeFlagInt(true);
                        } else {
                          console.error(
                            'Invalid Accounting Format value:',
                            value
                          );
                          return;
                        }

                        // Trigger the API call to save the changes
                        // React 18's automatic batching handles state updates
                        setSendEditDataFlag(prev => !prev);
                      }}
                    />
                  </Flex>

                  {/* Calendar Type - only display when periodCalTypeFlagInt is true or item.period_cal is true */}
                  {(periodCalTypeFlagInt || item.period_cal === true) && (
                    <Flex direction="column" width="25rem" margin="0 0 16px 0">
                      <BodyText
                        emphasis
                        style={{
                          color:
                            'var(--semantic-color-text-neutral-lightest-none)',
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          letterSpacing: '0.2px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        Calendar Type
                      </BodyText>
                      <SelectMenu
                        options={periodTypes.map(item => {
                          return {
                            value: item.value[0],
                            label: item.label[0],
                          };
                        })}
                        initialValue={item.period_cal_type}
                        style={{ height: '40px', width: '100%' }}
                        onChange={(_, value) => {
                          // No need to call onItemClick here as the item is already selected
                          // when the accordion is expanded

                          // Validate the value before updating and triggering API call
                          if (!value || value === '') {
                            console.error('Cannot set empty Calendar Type');
                            return;
                          }

                          // Update the periodCalTypeInt value
                          onPeriodCalTypeChange(value);

                          // Trigger the API call to save the changes
                          // React 18's automatic batching handles state updates
                          setSendEditDataFlag(prev => !prev);
                        }}
                      />
                    </Flex>
                  )}

                  {/* Save button - only show when simplifiedIntegrationFlow is enabled and integration name has changed */}
                  {simplifiedIntegrationFlow && hasAppNameChanged(item) && (
                    <Flex
                      direction="column"
                      margin="0 0 16px 0"
                      justifyContent="flex-end"
                      style={{ marginLeft: 'auto' }}
                    >
                      {/* Spacer to align with other fields that have labels */}
                      <div style={{ height: '24px' }} />
                      <Button
                        variant="filled"
                        onClick={() => saveAppName(item)}
                        disabled={savingItems[item.id] || !isAppNameValid(item)}
                        style={{
                          backgroundColor:
                            'var(--semantic-color-container-brand-strong-none)',
                          borderRadius: '96px',
                          height: '40px',
                        }}
                      >
                        {savingItems[item.id] ? 'Saving...' : 'Save'}
                      </Button>
                    </Flex>
                  )}

                  {/* Quickbooks Desktop specific fields */}
                  {item.integration_type === 'offline' && (
                    <Flex direction="row" gap={4} padding="0px" mb={6}>
                      {/* Date Range for data in Excel upload */}
                      <Flex direction="column" width="25rem" margin="0px">
                        <BodyText
                          emphasis
                          style={{
                            color:
                              'var(--semantic-color-text-neutral-lightest-none)',
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            fontWeight: 500,
                            letterSpacing: '0.2px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          Date Range for data in Excel upload
                        </BodyText>
                        <DateRangePicker
                          appearance="default"
                          cleanable={false}
                          placeholder="Date Range"
                          placement={'topStart'}
                          menuAutoWidth={
                            window.screen.width > 500 ? false : true
                          }
                          className="reskinned-date-picker"
                          block
                          size="sm"
                          showOneCalendar
                          format={store.getState().dateFormat.value}
                          onOk={value => onDateRangeChange(value, item.id)}
                        />
                      </Flex>

                      {/* File Upload Area */}
                      <Flex
                        width={'100%'}
                        justifyContent={'center'}
                        margin="0 0 16px 0"
                      >
                        <Uploader
                          listType="picture-text"
                          action={`${apiEndpoint}/api/xls_fileupload_user/`}
                          draggable
                          autoUpload
                          headers={
                            getAuthHeader()
                              ? { Authorization: getAuthHeader() }
                              : {}
                          }
                          method="POST"
                          data={excelUploadData}
                          multiple={false}
                          accept=".xls,.csv,.xlsx"
                          disabledFileItem
                          disabled={
                            excelUploadData['value'] === ''
                              ? true
                              : limitUploader[item.id] === undefined
                                ? false
                                : true
                          }
                          onChange={filelist =>
                            onUploaderChange(filelist, item.id, limitUploader)
                          }
                        >
                          <div
                            style={{
                              width: '100%',
                              height: 200,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px dashed #BBC0CE',
                              borderRadius: '8px',
                              padding: '16px',
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'Poppins',
                                fontSize: '16px',
                                color:
                                  'var(--semantic-color-text-neutral-strongest-none)',
                                textAlign: 'center',
                              }}
                            >
                              Click or Drag files to this area to upload. Files
                              will get uploaded automatically
                            </span>
                          </div>
                        </Uploader>
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Box>
            )}
          </Box>
        );
      })}
    </Flex>
  );
};

export default ReskinnedIntegrationList;
